import { useMemo } from 'react'
import { FeatureFlag, useFetchUserStatusQuery } from '@/api/user-api'
import { TourOptions } from '@/components/tour/onboarding-tour-modal/types'
import { CountryCode } from '@/constants/country-code'
import { Page } from '@/hooks/use-authorization/constants'
import routes from '@/hooks/use-authorization/routes'
import useFeatureFlags from '@/hooks/use-feature-flags'
import { useIntl } from '@/i18n/hooks/use-intl'
import isNotNil from '@/utils/is-not-nil'

export const useOnboardingTourOptions = (): TourOptions[] => {
  const { formatMessage } = useIntl()
  const { withFeature } = useFeatureFlags()
  const { data: userStatus } = useFetchUserStatusQuery()

  return useMemo((): TourOptions[] => {
    return [
      {
        iconName: 'search-kol-filled',
        tourTitle: formatMessage({ id: 'tour:title_kol_search' }),
        tourCaption: formatMessage({ id: 'tour:caption_kol_search' }),
        launchButtonText: formatMessage({ id: 'tour:button_kol_search' }),
        key: 'search-kol',
        permission: true,
        initUrl: `${Page.Search}?mode=kol`,
      },
      {
        iconName: 'social-performance-filled',
        tourTitle: formatMessage({ id: 'tour:title_kol_detail' }),
        tourCaption: formatMessage({ id: 'tour:caption_kol_detail' }),
        launchButtonText: formatMessage({
          id: 'tour:button_kol_detail',
        }),
        key: 'social-performance',
        permission: true,
        initUrl: `${Page.Search}?mode=kol`,
      },
      {
        iconName: 'predict-competitor-filled',
        tourTitle: formatMessage({ id: 'tour:title_competitive_analysis' }),
        tourCaption: formatMessage({ id: 'tour:caption_competitive_analysis' }),
        launchButtonText: formatMessage({
          id: 'tour:button_competitive_analysis',
        }),
        key: 'predict-competitor',
        permission:
          isNotNil(userStatus?.currentWorkspace.countryCode) &&
          [CountryCode.TW, CountryCode.JP].includes(
            userStatus.currentWorkspace.countryCode,
          ),
        initUrl: routes.competitiveBrandAnalyses.stringifyUrl({}),
      },
      {
        iconName: 'marketing-trend-filled',
        tourTitle: formatMessage({ id: 'tour:title_trend_analysis' }),
        tourCaption: formatMessage({ id: 'tour:caption_trend_analysis' }),
        launchButtonText: formatMessage({ id: 'tour:button_trend_analysis' }),
        key: 'marketing-trend',
        permission: true,
        initUrl: Page.HashtagBillboard,
      },
      {
        iconName: 'predictive-report-filled',
        tourTitle: formatMessage({
          id: 'tour:title_predictive_report',
        }),
        tourCaption: formatMessage({ id: 'tour:caption_predictive_report' }),
        launchButtonText: formatMessage({
          id: 'tour:button_predictive_report',
        }),
        key: 'predictive-report',
        permission: withFeature(FeatureFlag.PredictiveReport),
        initUrl: Page.PredictiveReport,
      },
      {
        iconName: 'report-performance-filled',
        tourTitle: formatMessage({ id: 'tour:title_report' }),
        tourCaption: formatMessage({ id: 'tour:caption_report' }),
        launchButtonText: formatMessage({
          id: 'tour:button_report',
        }),
        key: 'report-performance',
        permission: true,
        initUrl: routes.insightReport.stringifyUrl({}),
      },
    ]
  }, [formatMessage, userStatus, withFeature])
}
